import React from "react";

import { PartnerMember } from "../../api/partners";
import { SnackbarSeverity } from "../../hooks/useSnackbar";

export interface MemberRowMutationArgs {
  appUserId: string;
  /** Applied to the row immediately, before `request` settles. */
  optimisticPatch: Partial<PartnerMember>;
  /** Performs the write; resolve with only the fields the server actually
   * returned so a stale sibling field can never ride along into the row. */
  request: () => Promise<Partial<PartnerMember>>;
  successMessage: (result: Partial<PartnerMember>) => string;
  errorMessage: string;
}

/**
 * One row-scoped optimistic mutation, shared by every per-member control in
 * the manage-members dialog (Staff flag, portal access, …). A single
 * in-flight id set is shared across all callers so two controls on the same
 * row disable together while either write is outstanding, while unrelated
 * rows stay interactive.
 */
export function useMemberRowMutation(
  setMembers: React.Dispatch<React.SetStateAction<PartnerMember[]>>,
  showSnackbar: (message: string, severity?: SnackbarSeverity) => void,
) {
  const [pendingIds, setPendingIds] = React.useState<Set<string>>(new Set());

  const isPending = React.useCallback(
    (appUserId: string) => pendingIds.has(appUserId),
    [pendingIds],
  );

  const mutate = React.useCallback(
    async ({
      appUserId,
      optimisticPatch,
      request,
      successMessage,
      errorMessage,
    }: MemberRowMutationArgs) => {
      setPendingIds((prev) => new Set(prev).add(appUserId));

      // Snapshot only the fields this call is about to overwrite, taken
      // inside the functional update so it reflects the freshest state —
      // never a stale value from the click-time closure — and rollback on
      // failure can restore exactly (and only) those fields.
      let previousValues: Partial<PartnerMember> = {};
      setMembers((prev) =>
        prev.map((m) => {
          if (m.appUserId !== appUserId) return m;
          previousValues = Object.fromEntries(
            (Object.keys(optimisticPatch) as (keyof PartnerMember)[]).map(
              (key) => [key, m[key]],
            ),
          );
          return { ...m, ...optimisticPatch };
        }),
      );

      try {
        const result = await request();
        setMembers((prev) =>
          prev.map((m) =>
            m.appUserId === appUserId ? { ...m, ...result } : m,
          ),
        );
        showSnackbar(successMessage(result));
      } catch (err) {
        setMembers((prev) =>
          prev.map((m) =>
            m.appUserId === appUserId ? { ...m, ...previousValues } : m,
          ),
        );
        showSnackbar(
          err instanceof Error ? err.message : errorMessage,
          "error",
        );
      } finally {
        setPendingIds((prev) => {
          const next = new Set(prev);
          next.delete(appUserId);
          return next;
        });
      }
    },
    [setMembers, showSnackbar],
  );

  return { mutate, isPending };
}
